const DEFAULT_MAX_RANGE_DAYS = 366;

const titleCase = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isNumeric = (val) =>
  typeof val === "number" && Number.isFinite(val);

const isDateLike = (val) =>
  typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val);

const renderCell = (value, format) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  if (format === "percent" && typeof value === "number") {
    return `${(value * 100).toFixed(2)}%`;
  }
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
};

const sortingFnFor = (sample) =>
  isNumeric(sample) ? "basic" : isDateLike(sample) ? "alphanumeric" : "alphanumeric";

const sampleValue = (rows, key) =>
  rows?.find((r) => r && r[key] !== null && r[key] !== undefined)?.[key];

export const deriveColumns = (rows) => {
  if (!rows || rows.length === 0) return [];
  const keys = new Set();
  rows.forEach((row) => Object.keys(row || {}).forEach((k) => keys.add(k)));
  return Array.from(keys).map((key) => ({
    accessorKey: key,
    header: titleCase(key),
    sortingFn: sortingFnFor(sampleValue(rows, key)),
    cell: ({ getValue }) => renderCell(getValue()),
  }));
};

export const explicitColumns = (specs, rows) =>
  specs.map(([key, header, meta]) => ({
    accessorKey: key,
    header,
    sortingFn: sortingFnFor(sampleValue(rows, key)),
    meta,
    cell: ({ getValue }) => {
      const value = getValue();
      if (meta?.formatter) return meta.formatter(value);
      return renderCell(value, meta?.format);
    },
  }));

const diffDays = (start, end) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

export const validateBaseFilters = ({
  filters,
  startDate,
  endDate,
  requireTeacher = false,
  maxRangeDays = DEFAULT_MAX_RANGE_DAYS,
}) => {
  if (!filters?.customer) {
    return { ok: false, message: "Select a customer to run this report." };
  }
  if (!startDate || !endDate) {
    return { ok: false, message: "Choose a start and end date." };
  }
  if (new Date(startDate) > new Date(endDate)) {
    return { ok: false, message: "Start date must be on or before end date." };
  }
  if (diffDays(startDate, endDate) > maxRangeDays) {
    return {
      ok: false,
      message: `Date range cannot exceed ${maxRangeDays} days.`,
    };
  }
  if (requireTeacher && !filters.teacher) {
    return { ok: false, message: "Select a teacher to run this report." };
  }
  return { ok: true };
};

export const buildReportParams = ({ filters, startDate, endDate }) => ({
  customer_name: filters.customer.name,
  start_date: startDate,
  end_date: endDate,
  school_id: filters.school?.value,
  teacher_id: filters.teacher?.value,
  standard_code: filters.standard?.code,
});

export const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), endDate: fmt(end) };
};
